import { normalizeSearchQuery, scoreQueryMatch } from "@cmmd-forge/shared/searchRanking";

/**
 * Search scoring for the provider model picker. Adapted from t3code's
 * ModelPickerContent — the original supports multiple provider instances
 * (custom Codex Personal, etc.) so it ranks instance display names too;
 * Forge's picker has one instance per provider kind, so the surface is
 * simpler. We keep `providerKind` as a fallback search field so typing
 * "cursor" still selects Cursor's models even if the user doesn't know the
 * exact model slug.
 */
export type ModelPickerSearchableModel = {
  /** Slug used as model id (e.g. "gpt-5.3-codex", "anthropic/claude-sonnet-4-6"). */
  slug: string;
  /** Display name shown in the row. */
  name: string;
  /** Provider kind — also indexed so typing the provider name surfaces its models. */
  providerKind: string;
};

const PROVIDER_FIELD_WEIGHT_PENALTY = 10;

function getSearchFields(model: ModelPickerSearchableModel): string[] {
  return [
    normalizeSearchQuery(model.name),
    normalizeSearchQuery(model.slug),
    normalizeSearchQuery(model.providerKind),
  ];
}

function scoreToken(field: string, token: string, fieldBase: number): number | null {
  return scoreQueryMatch({
    value: field,
    query: token,
    exactBase: fieldBase,
    prefixBase: fieldBase + 2,
    boundaryBase: fieldBase + 4,
    includesBase: fieldBase + 6,
    ...(token.length >= 3 ? { fuzzyBase: fieldBase + 100 } : {}),
  });
}

/**
 * Score `model` against `query`. Returns `null` when no token matches any
 * field — caller filters those out. Lower scores rank higher. Returns `0`
 * when the query is empty so the caller can render the full list.
 */
export function scoreModelPickerSearch(
  model: ModelPickerSearchableModel,
  query: string,
): number | null {
  const tokens = normalizeSearchQuery(query)
    .split(/\s+/u)
    .filter((token) => token.length > 0);

  if (tokens.length === 0) return 0;

  const fields = getSearchFields(model);
  let total = 0;

  for (const token of tokens) {
    const tokenScores = fields
      .map((field, index) => scoreToken(field, token, index * PROVIDER_FIELD_WEIGHT_PENALTY))
      .filter((score): score is number => score !== null);

    if (tokenScores.length === 0) return null;
    total += Math.min(...tokenScores);
  }

  return total;
}

/**
 * Rank a list of models by query. Stable sort: equal scores preserve input
 * order so the upstream-defined ordering (e.g. "auto" first for Cursor)
 * stays intact when search is empty or all results tie.
 */
export function rankModelsByQuery<M extends ModelPickerSearchableModel>(
  models: ReadonlyArray<M>,
  query: string,
): ReadonlyArray<M> {
  if (!query.trim()) return models;
  const scored: Array<{ model: M; score: number; index: number }> = [];
  for (let index = 0; index < models.length; index += 1) {
    const model = models[index]!;
    const score = scoreModelPickerSearch(model, query);
    if (score === null) continue;
    scored.push({ model, score, index });
  }
  scored.sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    return a.index - b.index;
  });
  return scored.map((entry) => entry.model);
}
